import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import type { DragOverEvent } from '@dnd-kit/core';
import { getSectionKey } from 'store/entities/helios/templateForms/utils/nameUniqueness';
import { getCollapsedNamesAfterFirst } from 'utils/section/formSectionDnd';

type CollapsedBeforeDrag<TTab> = {
  tab: TTab | undefined;
  sections: string[];
};

type UseExpandSectionsForDragParams<TTab = unknown> = {
  collapsedSections: string[];
  setCollapsedSections: Dispatch<SetStateAction<string[]>>;
  setActiveDragItem: (item: null) => void;
  tab?: TTab;
  currentTabSections?: Array<{ name?: string | null }>;
};

export const useExpandSectionsForDrag = <TTab = unknown>({
  collapsedSections,
  setCollapsedSections,
  setActiveDragItem,
  tab,
  currentTabSections,
}: UseExpandSectionsForDragParams<TTab>) => {
  const collapsedBeforeDragRef = useRef<CollapsedBeforeDrag<TTab> | null>(null);

  const snapshotCollapsed = useCallback(() => {
    if (collapsedBeforeDragRef.current === null) {
      collapsedBeforeDragRef.current = { tab, sections: collapsedSections };
    }
  }, [collapsedSections, tab]);

  const startDrag = useCallback(() => {
    snapshotCollapsed();
  }, [snapshotCollapsed]);

  const expandSectionForDrag = useCallback(
    (sectionName?: string) => {
      if (!sectionName) return;
      snapshotCollapsed();
      const saved = collapsedBeforeDragRef.current;
      if (!saved || !saved.sections.includes(sectionName)) return;
      setCollapsedSections(saved.sections.filter((name) => name !== sectionName));
    },
    [setCollapsedSections, snapshotCollapsed]
  );

  const restoreSectionsAfterDrag = useCallback(
    (keepExpandedName?: string) => {
      const saved = collapsedBeforeDragRef.current;
      collapsedBeforeDragRef.current = null;

      if (!saved) return;

      if (tab !== undefined && saved.tab !== tab) {
        setCollapsedSections(getCollapsedNamesAfterFirst(currentTabSections, keepExpandedName));
        return;
      }

      setCollapsedSections(keepExpandedName ? saved.sections.filter((name) => name !== keepExpandedName) : saved.sections);
    },
    [currentTabSections, setCollapsedSections, tab]
  );

  const handleDragCancel = useCallback(() => {
    restoreSectionsAfterDrag();
    setActiveDragItem(null);
  }, [restoreSectionsAfterDrag, setActiveDragItem]);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const activeType = (event.active.data.current as { type?: string } | undefined)?.type;
      if (activeType === 'section' || activeType === 'tab') return;

      const overData = event.over?.data.current as { name?: unknown; tabId?: string | null } | undefined;
      if (typeof overData?.name !== 'string') return;

      const overIdentity = 'tabId' in overData ? getSectionKey(overData.name, overData.tabId) : overData.name;
      expandSectionForDrag(overIdentity);
    },
    [expandSectionForDrag]
  );

  return { startDrag, expandSectionForDrag, restoreSectionsAfterDrag, handleDragCancel, handleDragOver };
};
